import { Col, Row } from "@/app/components/lib/my-bootstrap";
import BtnQuantitySelector from "./btn-quantity-selector/BtnQuantitySelector";
import SvgCartDisabled from "./details-add-to-card-icons/SvgCartDisabled";
import SvgDetailsAddToCart from "./details-add-to-card-icons/SvgDetailsAddToCart";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  ICartItemExtraFields,
  IuseProductHasAttrsResult,
} from "@/hooks/product-has-attrs/product-has-attrs-helpers/interfaces";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import Price from "@/app/components/lib/Price";
import { goToCartAtom } from "../../../common/go-to-cart-pop-up/GoToCartPopUp";
import { useAtom } from "jotai";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { adaptFieldsToPost } from "@/app/components/common-form-builder/form-builder-helpers/form-functions";
import { Navbar } from "@/utils/bootstrap";
import { Link } from "@/app/components/lib/navigation/Link";
import { PagesPaths } from "@/utils/functions/pages-paths";
import NotFieldErrModal from "@/app/components/common-form-builder/form-builder-helpers/NotFieldErrModal";
import { useTranslation } from "@/hooks/localization/useTranslations";
import useProductPopup from "@/hooks/useProductPopup";
import ProtectedContent from "@/app/components/common-builder/product/product-box/product-box-helpers/ProtectedContent";
import useSellingMode from "@/hooks/useSellingMode";
import { ProductPreviewTypes } from "@/api/AppApi/services/product/interfaces/api-product-details-interface";
import SvgRegisterArrow from "./details-add-to-card-icons/SvgRegisterArrow";
import { fromProductPreviewTypeToCartItemType } from "../../../cart/cart-functions";
import { roundToFourDecimals } from "@/utils/functions/functions";

interface Props {
  productState: IuseProductHasAttrsResult;
  form: UseFormReturn<FieldValues, any>;
  isPopupMobile: boolean;
  agreeTerms: boolean;
  popup?: "normal" | "configrable" | false;
}

const DetailsAddToCartBtn: React.FC<Props> = ({
  productState,
  form,
  isPopupMobile,
  agreeTerms,
  popup = false,
}) => {
  const {
    cartDisabled,
    handleAddToCart,
    handleQuantityChange,
    quantity,
    shouldConfig,
    variantNotExist,
    requiredAddonsNotChosen,
    outOfStock,
    addedToCart,
    subTotalPrice,
    plusDisabled,
    notFieldErr,
    setNotFieldErr,
    currentProd,
    selectedUnitId,
    disableRegistrationEvent,
  } = productState;
  const isEvent = currentProd.preview_type === ProductPreviewTypes.event;
  const { min_quantity_in_cart, units } = currentProd;
  const selectedUnit = units.find((unit) => unit.id === selectedUnitId);
  const { enableAddToCart } = useSellingMode();
  const getCompany = useGetCompanyQuery();
  const t = useTranslation();
  const addToCartTxt =
    getCompany?.data?.result?.theme?.components?.product?.add_to_cart_btn_title;
  const mobile = useIsMobile();
  const [, setOpenGoToCardModal] = useAtom(goToCartAtom);
  const { handleClosePopup } = useProductPopup();
  const noClickEvent = addedToCart || cartDisabled || !agreeTerms;
  const addToCartBtn = (
    <ProtectedContent showChildren={enableAddToCart}>
      <button
        // type="submit"
        onClick={
          noClickEvent
            ? undefined
            : form.handleSubmit((data, e) => {
                e?.stopPropagation();
                const other_inputs = adaptFieldsToPost(data);
                handleAddToCart(
                  {
                    type: fromProductPreviewTypeToCartItemType(
                      currentProd.preview_type,
                    ),
                  } as unknown as ICartItemExtraFields,
                  other_inputs,
                );
                setOpenGoToCardModal(true);
              })
        }
        className={`btn-product-add-to-cart ${
          cartDisabled || !agreeTerms
            ? "disabled"
            : addedToCart
            ? "added-to-cart"
            : ""
        }`}
      >
        <>
          {cartDisabled || !agreeTerms ? (
            <div className="cart-disabled-text w-100">
              <SvgCartDisabled />
              <p>
                {shouldConfig
                  ? t.choose_variant
                  : variantNotExist
                  ? t.this_variant_not_available
                  : outOfStock
                  ? t.out_of_stock
                  : requiredAddonsNotChosen
                  ? t.choose_required_addons
                  : !agreeTerms
                  ? t.you_should_agree_to_terms_conditions
                  : disableRegistrationEvent
                  ? t.registration_disabled_for_now
                  : t.sell_disabled}
              </p>
            </div>
          ) : !addedToCart ? (
            <div
              className={`not-added-to-cart-text w-100 ${
                isEvent
                  ? "d-flex flex-row-reverse justify-content-center align-items-center"
                  : ""
              }`}
            >
              {isEvent ? <SvgRegisterArrow /> : <SvgDetailsAddToCart />}
              {isEvent
                ? t.register_now
                : addToCartTxt
                ? addToCartTxt
                : t.add_to_cart}
            </div>
          ) : (
            <>
              <Link
                prefetch={false}
                href={PagesPaths.cart}
                onClick={() => handleClosePopup()}
              >
                <Row className="m-0 align-items-center added-to-cart-text">
                  <Col className="p-0">
                    <Row className="m-0 go-to-cart">{`${t.go_to_cart} (${quantity})`}</Row>
                    <Row className="m-0 flex flex-nowrap align-items-center justify=content-center">
                      {`${t.subtotal}:`}
                      <Price
                        price={{
                          value: roundToFourDecimals(subTotalPrice * quantity),
                          className: "price",
                        }}
                        roundPrice
                      />
                    </Row>
                  </Col>
                </Row>
              </Link>
              <BtnQuantitySelector
                minQuantity={
                  selectedUnit?.min_quantity_in_cart &&
                  selectedUnit?.min_quantity_in_cart > 0
                    ? selectedUnit?.min_quantity_in_cart
                    : min_quantity_in_cart ?? 1
                }
                quantity={quantity}
                setQuantity={handleQuantityChange}
                plusDisabled={plusDisabled}
                addedToCart={addedToCart}
                disableChangeQuantity={isEvent}
              />
            </>
          )}
        </>
      </button>
    </ProtectedContent>
  );

  return (
    <>
      {notFieldErr.length > 0 && (
        <NotFieldErrModal errors={notFieldErr} setErrors={setNotFieldErr} />
      )}

      {mobile ? (
        <Navbar
          fixed="bottom"
          className={`mobile-place-order-btn ${isPopupMobile ? "vw-100" : ""}`}
        >
          {addToCartBtn}
        </Navbar>
      ) : (
        <div className={popup === false ? "col-7 col" : undefined}>
          {addToCartBtn}
        </div>
      )}
    </>
  );
};

export default DetailsAddToCartBtn;
